/**
 * ⌘K: the sections the role has, plus clients, deals and bookings from the API as the person
 * types. A number finds a deal by its №, a phone-shaped query is matched through the clients'
 * `?phone=` lookup that normalises it (§3.9), and a source the role may not read answers nothing
 * rather than failing the rest.
 */
import { useQuery } from "@tanstack/react-query";
import { Briefcase, Ticket, UserRound } from "lucide-react";
import { lazy, Suspense, useEffect, useState, type JSX } from "react";
import { useNavigate } from "react-router";

import { bookingsApi } from "@/api/bookings";
import { crmApi } from "@/api/endpoints";
import { queryKeys } from "@/api/queryKeys";
import { useTranslation } from "@/i18n";
import { canOpenBookings } from "@/lib/permissions";
import { useAuthStore } from "@/store/auth";
import type { PaletteGroup, PaletteItem } from "@/ui/command-palette";

import { bottomNav, mainNav } from "./navigation";
import { searchParamsFor } from "./searchQuery";

// Loaded on the first ⌘K, so the command library stays out of the first paint.
const CommandPalette = lazy(async () => {
  const module = await import("@/ui/command-palette");
  return { default: module.CommandPalette };
});

const DEBOUNCE_MS = 200;
const MAX_PER_KIND = 5;

async function fulfilled<T>(request: Promise<{ results: T[] }>): Promise<T[]> {
  try {
    return (await request).results.slice(0, MAX_PER_KIND);
  } catch {
    // A source this role cannot read, or that is down, leaves the others standing.
    return [];
  }
}

export function SearchPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const [query, setQuery] = useState("");
  const [term, setTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(query.trim());
    }, DEBOUNCE_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setTerm("");
    }
  }, [open]);

  const results = useQuery({
    queryKey: queryKeys.shellSearch(term),
    enabled: open && term.length >= 2,
    queryFn: async () => {
      const params = searchParamsFor(term);
      const [clients, deals, bookings] = await Promise.all([
        fulfilled(crmApi.clients(params.clients)),
        fulfilled(crmApi.deals(params.deals)),
        canOpenBookings(role) ? fulfilled(bookingsApi.list({ q: term })) : Promise.resolve([]),
      ]);
      return { clients, deals, bookings };
    },
  });

  if (role === undefined) return null;
  const go = (to: string) => (): void => {
    void navigate(to);
  };
  const needle = query.trim().toLowerCase();
  const sections: PaletteItem[] = [...mainNav(role), ...bottomNav(role)]
    .map((item) => {
      const Icon = item.icon;
      return {
        key: `nav-${item.key}`,
        label: t(item.labelKey),
        hint: t("shell.palette.kind.section"),
        icon: <Icon size={16} />,
        onSelect: go(item.to),
      };
    })
    .filter((item) => needle === "" || item.label.toLowerCase().includes(needle));
  const found: PaletteItem[] = [
    ...(results.data?.clients ?? []).map((client) => ({
      key: `client-${String(client.id)}`,
      label: client.phone === "" ? client.full_name : `${client.full_name} · ${client.phone}`,
      hint: t("shell.palette.kind.client"),
      icon: <UserRound strokeWidth={1.5} />,
      onSelect: go(`/mijozlar/${String(client.id)}`),
    })),
    ...(results.data?.deals ?? []).map((deal) => ({
      key: `deal-${String(deal.id)}`,
      label: t("shell.palette.deal", { id: String(deal.id), name: deal.client_name }),
      hint: t("shell.palette.kind.deal"),
      icon: <Briefcase strokeWidth={1.5} />,
      onSelect: go(`/bitimlar/${String(deal.id)}`),
    })),
    ...(results.data?.bookings ?? []).map((booking) => ({
      key: `booking-${String(booking.id)}`,
      label: t("shell.palette.booking", { ref: booking.external_id, name: booking.operator_name }),
      hint: t("shell.palette.kind.booking"),
      icon: <Ticket strokeWidth={1.5} />,
      onSelect: go(`/bronlar?booking=${String(booking.id)}`),
    })),
  ];
  const groups: readonly PaletteGroup[] = [{ items: [...found, ...sections] }];

  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <CommandPalette
        open={open}
        onOpenChange={onOpenChange}
        title={t("shell.palette.title")}
        placeholder={t("shell.palette.searchPlaceholder")}
        emptyLabel={t("shell.palette.empty")}
        groups={groups}
        query={query}
        onQueryChange={setQuery}
        loading={results.isFetching}
      />
    </Suspense>
  );
}
